import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Activity } from 'lucide-react';
import { Heading } from '../layout';
import Table2 from '../reusable/Table2';
import { apiClient, API_BASE_URL } from '../../services/apiClient';
import AdministrationBackLink from '../system/AdministrationBackLink';

const formatWhen = (iso) => {
  if (!iso) return '—';
  try {
    return new Date(iso).toLocaleString('es-CL');
  } catch {
    return String(iso);
  }
};

const COLUMNS = [
  { key: 'when', title: 'Fecha', width: 170, align: 'left' },
  { key: 'actor', title: 'Actor', width: 180, align: 'left' },
  { key: 'action', title: 'Acción', width: 200, align: 'left' },
  { key: 'resource', title: 'Recurso', width: 160, align: 'left' },
  { key: 'outcome', title: 'Resultado', width: 100, align: 'center' },
  { key: 'ip_address', title: 'IP', width: 120, align: 'left' },
];

const SystemActivityWorkspace = ({ isDarkMode = false, showAdminBack = false }) => {
  const [rawItems, setRawItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [actionFilter, setActionFilter] = useState('');
  const [actorFilter, setActorFilter] = useState('');
  const [selected, setSelected] = useState(null);

  const loadLogs = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const params = new URLSearchParams({ limit: '100', offset: '0' });
      if (actionFilter.trim()) params.set('action', actionFilter.trim());
      if (actorFilter.trim()) params.set('actor', actorFilter.trim());
      const data = await apiClient(`${API_BASE_URL}/api/audit?${params.toString()}`);
      setRawItems(Array.isArray(data?.items) ? data.items : []);
      setTotal(Number(data?.total) || 0);
    } catch (err) {
      console.error(err);
      setError(err.message || 'No se pudo cargar el registro de actividad.');
      setRawItems([]);
      setTotal(0);
    } finally {
      setLoading(false);
    }
  }, [actionFilter, actorFilter]);

  useEffect(() => {
    loadLogs();
  }, [loadLogs]);

  const tableRows = useMemo(
    () =>
      rawItems.map((row) => ({
        id: row.id,
        when: formatWhen(row.created_at),
        actor: row.actor_name || row.actor_email || '—',
        action: row.action || '—',
        resource: [row.resource_type, row.resource_id].filter(Boolean).join(' #') || '—',
        outcome: row.outcome || '—',
        ip_address: row.ip_address || '—',
        _raw: row,
      })),
    [rawItems]
  );

  return (
    <div>
      <Heading level={2} isDarkMode={isDarkMode}>
        <Activity size={20} style={{ marginRight: '8px', verticalAlign: 'middle' }} />
        Actividad del Sistema
      </Heading>
      {showAdminBack ? <AdministrationBackLink /> : null}

      <div
        style={{
          display: 'flex',
          flexWrap: 'wrap',
          gap: 8,
          marginBottom: 12,
          alignItems: 'center',
        }}
      >
        <input
          type="text"
          placeholder="Filtrar acción…"
          value={actionFilter}
          onChange={(e) => setActionFilter(e.target.value)}
          style={{ padding: '6px 10px', minWidth: 160 }}
        />
        <input
          type="text"
          placeholder="Filtrar actor…"
          value={actorFilter}
          onChange={(e) => setActorFilter(e.target.value)}
          style={{ padding: '6px 10px', minWidth: 160 }}
        />
        <button type="button" onClick={loadLogs} style={{ padding: '6px 12px' }}>
          Actualizar
        </button>
      </div>

      {error ? (
        <p style={{ color: '#c53030', marginBottom: 12 }}>{error}</p>
      ) : null}

      <Table2
        title="Registro de auditoría"
        subtitle={loading ? 'Cargando…' : `${tableRows.length} de ${total} eventos`}
        data={tableRows}
        columns={COLUMNS}
        searchable
        filterable={false}
        sortable
        exportable={false}
        importable={false}
        selectable={false}
        showActions={false}
        pageSize={50}
        isDarkMode={isDarkMode}
        onRowClick={(row) => setSelected(row._raw || row)}
      />

      {selected ? (
        <div
          role="dialog"
          style={{
            position: 'fixed',
            inset: 0,
            background: 'rgba(0,0,0,0.45)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 1000,
          }}
          onClick={() => setSelected(null)}
        >
          <div
            style={{
              background: isDarkMode ? '#1a1a1a' : '#fff',
              color: isDarkMode ? '#eee' : '#111',
              padding: 20,
              borderRadius: 8,
              maxWidth: 560,
              width: '90%',
              maxHeight: '80vh',
              overflow: 'auto',
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <h3 style={{ marginTop: 0 }}>Detalle del evento</h3>
            <pre style={{ whiteSpace: 'pre-wrap', fontSize: 12 }}>
              {JSON.stringify(selected, null, 2)}
            </pre>
            <button type="button" onClick={() => setSelected(null)}>
              Cerrar
            </button>
          </div>
        </div>
      ) : null}
    </div>
  );
};

export default SystemActivityWorkspace;
